import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Award, Building2, Check, ShieldCheck, Sparkles, Star } from "lucide-react";
import { CLINIC, IMAGES } from "../lib/content";

const fade = (delay = 0, y = 20) => ({
  initial: { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] },
});

const float = (delay = 0) => ({
  animate: { y: [0, -8, 0] },
  transition: { duration: 6, delay, repeat: Infinity, ease: "easeInOut" },
});

export default function HeroPreview() {
  return (
    <div className="min-h-screen w-full bg-[#EFE9E3] font-sans flex items-center justify-center p-3 sm:p-6 lg:p-12">
      <motion.div
        {...fade(0, 30)}
        className="relative w-full max-w-[1400px] bg-white rounded-[1.5rem] sm:rounded-[2rem] lg:rounded-[2.5rem] overflow-hidden"
        style={{ boxShadow: "0 60px 120px -40px rgba(94,39,65,0.28), 0 20px 40px -20px rgba(94,39,65,0.15)" }}
      >
        {/* Organic tinted shape on the right */}
        <div className="absolute inset-y-0 right-0 w-[58%] pointer-events-none">
          <svg viewBox="0 0 700 900" preserveAspectRatio="none" className="w-full h-full">
            <defs>
              <linearGradient id="pinkGrad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#F7E6EA" />
                <stop offset="0.55" stopColor="#EAC9D0" />
                <stop offset="1" stopColor="#D9A9B4" />
              </linearGradient>
            </defs>
            <path
              d="M140,0 C40,140 100,320 90,470 C80,620 200,720 240,900 L700,900 L700,0 Z"
              fill="url(#pinkGrad)"
            />
          </svg>
        </div>

        {/* Faint arc lines */}
        <svg className="absolute inset-0 pointer-events-none" viewBox="0 0 1400 900" preserveAspectRatio="none">
          <path d="M-200,720 Q400,300 1600,760" stroke="#5E2741" strokeOpacity="0.05" strokeWidth="1" fill="none" />
          <path d="M-100,820 Q500,420 1700,860" stroke="#5E2741" strokeOpacity="0.04" strokeWidth="1" fill="none" />
        </svg>

        {/* Header */}
        <header className="relative z-20 flex items-center justify-between px-5 sm:px-10 lg:px-14 pt-6 sm:pt-8">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-2xl bg-primary grid place-items-center text-primary-foreground font-serif text-lg sm:text-xl">M</div>
            <span className="font-serif text-xl sm:text-2xl text-primary">Mom &amp; More</span>
          </div>
          <nav className="hidden lg:flex items-center gap-10 text-sm font-medium text-foreground/85">
            <a href="#about" className="hover:text-primary transition-colors">About</a>
            <a href="#reels" className="hover:text-primary transition-colors">Reels</a>
            <a href="#stories" className="hover:text-primary transition-colors">Stories</a>
            <a href="#visit" className="hover:text-primary transition-colors">Visit</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href={`tel:${CLINIC.phone.replace(/\s/g, "")}`} className="hidden md:inline-flex text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
              {CLINIC.phone}
            </a>
            <a
              href="/#booking"
              data-testid="preview-signup"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-border pl-1.5 pr-4 sm:pr-5 py-1.5 text-sm font-semibold text-primary hover:-translate-y-0.5 transition-transform"
            >
              <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary grid place-items-center text-primary-foreground">
                <Check size={13} />
              </span>
              Book
            </a>
          </div>
        </header>

        {/* Main content grid */}
        <div className="relative z-10 grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-2 items-start px-5 sm:px-10 lg:px-14 pt-8 sm:pt-14 pb-12">
          {/* Left */}
          <div className="pt-2 lg:pt-14">
            <motion.span {...fade(0.1)} className="overline text-accent block mb-5 sm:mb-6">
              Obstetrics &amp; Gynaecology · 25 years
            </motion.span>

            <motion.h1
              {...fade(0.2)}
              className="font-serif tracking-tight leading-[0.95] text-[2.35rem] xs:text-[2.75rem] sm:text-[3.75rem] lg:text-[5.5rem]"
            >
              <span className="block text-primary">Women&rsquo;s health</span>
              <span className="block italic" style={{
                background: "linear-gradient(90deg,#C48A9A 0%,#A6788B 45%,#7A9A8C 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>
                &amp; motherhood,
              </span>
              <span className="block text-foreground">in gentle hands.</span>
            </motion.h1>

            <motion.p {...fade(0.35)} className="mt-6 sm:mt-8 max-w-md text-muted-foreground text-sm sm:text-base lg:text-lg leading-relaxed">
              Compassionate, evidence-led consultations with{" "}
              <span className="text-foreground font-semibold">{CLINIC.doctor}</span> — {CLINIC.credentialsShort}. Based at {CLINIC.name}, Mahadevapura.
            </motion.p>

            <motion.a
              {...fade(0.5)}
              href="/#booking"
              data-testid="preview-cta"
              className="group mt-8 sm:mt-10 inline-flex items-center gap-3 sm:gap-4 rounded-full bg-primary text-primary-foreground pl-1.5 sm:pl-2 pr-6 sm:pr-8 py-1.5 sm:py-2 hover:-translate-y-0.5 transition-transform"
            >
              <span className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white text-primary grid place-items-center group-hover:rotate-45 transition-transform duration-500">
                <ArrowRight size={18} />
              </span>
              <span className="text-sm sm:text-base font-semibold tracking-tight">Get Appointment</span>
            </motion.a>

            {/* Mobile/tablet inline stat strip (hidden on lg where floating cards take over) */}
            <motion.div {...fade(0.65)} className="mt-10 grid grid-cols-2 gap-3 lg:hidden">
              <div className="rounded-2xl border border-border bg-white p-4">
                <p className="font-serif text-primary text-2xl leading-none">25 yrs</p>
                <p className="text-[0.7rem] text-muted-foreground mt-2">Clinical practice</p>
              </div>
              <div className="rounded-2xl border border-border bg-white p-4">
                <p className="font-serif text-primary text-lg leading-tight">MRCOG · MRCPI</p>
                <p className="text-[0.7rem] text-muted-foreground mt-1">London &amp; Ireland</p>
              </div>
              <div className="rounded-2xl border border-border bg-white p-4">
                <p className="text-xs font-semibold text-foreground leading-tight">Fertility &amp; Aesthetic Gynaecology</p>
                <p className="text-[0.65rem] text-muted-foreground mt-1">FRM · FMAS</p>
              </div>
              <div className="rounded-2xl border border-border bg-white p-4">
                <div className="flex items-center gap-1.5">
                  <span className="flex text-primary">
                    {[...Array(5)].map((_, i) => <Star key={i} size={11} fill="currentColor" strokeWidth={0} />)}
                  </span>
                  <span className="text-sm font-semibold text-foreground">5.0</span>
                </div>
                <p className="text-[0.7rem] text-muted-foreground mt-1.5">180 Google reviews</p>
              </div>
            </motion.div>

            <motion.div {...fade(0.75)} className="hidden lg:flex mt-14 items-center gap-8">
              <div className="flex items-center gap-2">
                <span className="flex text-primary">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" strokeWidth={0} />)}
                </span>
                <span className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">5.0</span> · 180 Google reviews
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShieldCheck size={16} className="text-accent" />
                MRCOG (London) &amp; MRCPI (Ireland)
              </div>
            </motion.div>
          </div>

          {/* Right — portrait & floating cards */}
          <div className="relative min-h-[380px] sm:min-h-[520px] lg:min-h-[640px] mt-4 lg:mt-0">
            {/* Portrait */}
            <motion.div
              {...fade(0.25, 40)}
              className="relative h-full flex items-end justify-center"
            >
              <img
                src={IMAGES.portrait}
                alt="Dr. Yasmin Imdad"
                className="max-h-[380px] sm:max-h-[520px] lg:max-h-[640px] w-auto object-contain object-bottom drop-shadow-[0_30px_40px_rgba(94,39,65,0.15)]"
              />
            </motion.div>

            {/* Floating: top-left — hospital affiliation */}
            <motion.div
              {...fade(0.9)}
              className="hidden lg:block absolute top-8 left-2 sm:left-6 z-20"
            >
              <motion.div {...float(0)} className="bg-white rounded-2xl px-4 py-3 border border-border shadow-lg flex items-center gap-3 max-w-[15rem]">
                <span className="w-9 h-9 rounded-lg bg-secondary/60 text-primary grid place-items-center">
                  <Building2 size={17} />
                </span>
                <div className="leading-tight">
                  <p className="text-[0.65rem] font-semibold text-muted-foreground uppercase tracking-wider">Affiliated</p>
                  <p className="text-xs font-semibold text-foreground mt-1">Rainbow Hospitals · BirthRight</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating: bottom-left — dual international accreditation */}
            <motion.div
              {...fade(1.05)}
              className="hidden lg:block absolute -bottom-4 left-0 sm:left-4 z-20"
            >
              <motion.div {...float(1.5)} className="bg-white rounded-2xl px-4 py-3.5 border border-border shadow-lg max-w-[18rem]">
                <div className="flex items-center gap-2">
                  <Award size={15} className="text-accent" />
                  <p className="text-[0.65rem] font-semibold text-muted-foreground uppercase tracking-wider">Royal College accreditation</p>
                </div>
                <p className="font-serif text-primary text-lg mt-1.5 leading-tight">MRCOG <span className="text-accent">·</span> MRCPI</p>
                <p className="text-[0.7rem] text-muted-foreground mt-0.5">London (UK) &amp; Ireland</p>
              </motion.div>
            </motion.div>

            {/* Floating: below-center — 5.0 rating */}
            <motion.div
              {...fade(1.2)}
              className="hidden lg:block absolute -bottom-6 left-1/2 -translate-x-1/2 z-20"
            >
              <motion.div {...float(2.5)} className="flex items-center gap-3 bg-white rounded-full pl-3 pr-5 py-2.5 border border-border shadow-lg">
                <span className="flex text-primary">
                  {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
                </span>
                <div className="leading-none">
                  <p className="text-xs font-semibold text-foreground"><span className="text-primary">5.0</span> · 180 reviews</p>
                  <p className="text-[0.6rem] text-muted-foreground mt-0.5">on Google</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating: bottom-right — Dr. Yasmin badge card (visible always) */}
            <motion.div
              {...fade(0.8)}
              className="absolute bottom-6 sm:bottom-10 lg:bottom-14 right-1 sm:right-4 z-20"
            >
              <motion.div {...float(1)} className="bg-white rounded-2xl p-1.5 sm:p-2 border border-border shadow-xl">
                <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-lg sm:rounded-xl overflow-hidden bg-primary">
                  <img src={IMAGES.portrait} alt="Dr. Yasmin" className="w-full h-full object-cover object-top" />
                </div>
                <div className="mt-1.5 sm:mt-2 rounded-md sm:rounded-lg bg-primary text-primary-foreground text-center px-2 sm:px-3 py-1 sm:py-1.5">
                  <p className="text-[0.6rem] sm:text-[0.7rem] font-semibold leading-none">Dr. Yasmin</p>
                  <p className="text-[0.5rem] sm:text-[0.55rem] opacity-80 mt-0.5 sm:mt-1 leading-none">Senior Consultant · OBG</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating: top-right — 25 years experience (visible always) */}
            <motion.div
              {...fade(1.0)}
              className="absolute top-2 sm:top-6 right-1 sm:right-4 z-20"
            >
              <motion.div {...float(0.5)} className="bg-white rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3 border border-border shadow-lg flex items-center gap-2 sm:gap-3">
                <span className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-primary text-primary-foreground grid place-items-center">
                  <span className="font-serif text-base sm:text-xl leading-none">25</span>
                </span>
                <div className="leading-tight">
                  <p className="text-[0.7rem] sm:text-xs font-semibold text-foreground">Years in practice</p>
                  <p className="text-[0.6rem] sm:text-[0.65rem] text-muted-foreground mt-0.5">Obstetrics &amp; Gynaecology</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating: mid-left — sub-specialty */}
            <motion.div
              {...fade(1.35)}
              className="hidden lg:block absolute top-1/2 -translate-y-8 left-0 sm:-left-2 z-20"
            >
              <motion.div {...float(3.2)} className="bg-white rounded-2xl px-4 py-3 border border-border shadow-lg max-w-[15rem]">
                <div className="flex items-center gap-2">
                  <Sparkles size={13} className="text-accent" />
                  <p className="text-[0.65rem] font-semibold text-muted-foreground uppercase tracking-wider">Sub-specialty</p>
                </div>
                <p className="text-sm font-semibold text-foreground mt-1.5 leading-tight">Fertility &amp; Aesthetic Gynaecology</p>
                <p className="text-[0.65rem] text-muted-foreground mt-0.5">FRM · FMAS Fellowships</p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom preview footer strip */}
        <div className="relative z-10 border-t border-border/60 px-5 sm:px-10 lg:px-14 py-4 sm:py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 bg-white/60 backdrop-blur">
          <p className="text-xs text-muted-foreground">
            Hero layout preview · This does not replace the live homepage.
          </p>
          <a
            href="/"
            data-testid="preview-back"
            className="inline-flex items-center gap-2 text-xs font-semibold text-primary hover:text-foreground transition-colors"
          >
            View live site <ArrowUpRight size={13} />
          </a>
        </div>
      </motion.div>
    </div>
  );
}
